import { Link } from "react-router-dom";

import type { Thread, ThreadCategory } from "@/lib/types";

export const CATEGORY_LABEL: Record<ThreadCategory, string> = {
  question: "question",
  bug: "bug",
};

// `category` is typed loosely on purpose: threads created before the "review" label was retired
// still carry it, and an unknown value should render as itself with the default icon rather than
// blowing a hole in the row.
export function CategoryIcon({ category, className = "h-3 w-3" }: { category: string; className?: string }) {
  if (category === "bug") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function CategoryChip({ category }: { category: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-[3px] border border-white/[0.14] bg-white/[0.03] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-text/55">
      <CategoryIcon category={category} />{" "}
      {CATEGORY_LABEL[category as ThreadCategory] ?? category}
    </span>
  );
}

// Mongo round-trips datetimes as naive (tzinfo dropped), so the API sometimes serializes
// them with no "Z"/offset — parsed as-is, the browser reads that as local time instead of
// UTC. Append "Z" when there's no timezone marker already (mirrors the same fix applied to
// version timestamps elsewhere in this file's sibling page).
function parseUtc(dateStr: string): Date {
  return new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`);
}

// "3d ago" — used throughout the discussions UI (thread rows, replies).
export function threadTimeAgo(dateStr: string): string {
  const diff = Date.now() - parseUtc(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function ThreadRow({ thread, href }: { thread: Thread; href: string }) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 border-b border-white/[0.06] py-3.5 transition hover:bg-white/[0.02]"
    >
      <CategoryChip category={thread.category} />
      <span className="min-w-0 flex-1 truncate font-display text-sm text-text/90">{thread.title}</span>
      {thread.reply_count > 0 && (
        <span className="hidden shrink-0 font-mono text-[11px] text-text/35 sm:inline">
          {thread.reply_count} {thread.reply_count === 1 ? "reply" : "replies"}
        </span>
      )}
      <span className={`shrink-0 font-mono text-[11px] ${thread.status === "answered" ? "text-text/45" : "text-primary/70"}`}>
        {thread.status}
      </span>
      <span className="shrink-0 font-mono text-[11px] text-text/35">{threadTimeAgo(thread.updated_at)}</span>
    </Link>
  );
}
